import { Stack, Typography } from '@mui/material';
import React, { cloneElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { JobLogsCompareVersionsButton } from '@/console/features/jobs/components/job-logs-compare-versions-button/job-logs-compare-versions-button';
import { CompareVersionsButton } from '@/console/features/projects/components/compare-versions-button/compare-versions-button';
import { CompareIcon } from '@/shared/assets/icons/compare-icon';
import { Button } from '@/shared/components/ui/button/button';
import { PopoverController } from '@/shared/components/ui/popover/components/popover-controller/popover-controller';
import { Trigger } from '@/shared/components/ui/trigger/trigger';
import { TTriggerComponentType } from '@/shared/components/ui/trigger/trigger.types';
import { useReadJobQuery } from '@/shared/redux/api/jobs/jobs-api';
import { JobFragment } from '@/shared/types/types.generated';
import { formatShortId } from '@/shared/utils/format';

import { TCompareJobsButtonProps } from './compare-jobs-button.types';

export const CompareJobsButton: React.FC<TCompareJobsButtonProps> = ({
  job,
  targetJob: selectedTargetJob,
  onCallback,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const targetJobId = job?.metaData?.target_job_id;
  const { data: targetJobData } = useReadJobQuery(
    { jobId: targetJobId! },
    { skip: !targetJobId || !!selectedTargetJob },
  );
  const resolvedTargetJob = selectedTargetJob ?? targetJobData?.readJob;

  const previousJobId = job?.metaData?.latest_successful_job_id_with_artifacts;
  const { data: previousJobData } = useReadJobQuery(
    { jobId: previousJobId! },
    { skip: !previousJobId },
  );
  const previousJob = previousJobData?.readJob;

  const jobHasArtifacts = job?.artifactsAvailable;
  const canCompareTarget =
    jobHasArtifacts &&
    resolvedTargetJob?.artifactsAvailable &&
    resolvedTargetJob.jobId !== job?.jobId;
  const canComparePrevious =
    jobHasArtifacts &&
    previousJob?.artifactsAvailable &&
    previousJobId !== job!.jobId &&
    !selectedTargetJob;
  const canCompareVersions = Boolean(
    job?.projectIds?.[0] &&
    job?.projectVersionIds?.[0] &&
    job?.metaData?.created_project_version_id,
  );
  const canCompareSelectedVersions = Boolean(
    selectedTargetJob &&
    job?.projectIds?.[0] &&
    job?.metaData?.created_project_version_id &&
    selectedTargetJob.metaData?.created_project_version_id,
  );

  const tooltip = (comparisonJobId: string, variant: 'target' | 'previous') => (
    <Stack direction="row" alignItems="center" gap="0.25rem">
      <Typography variant="p8">{t('pipelines:base_job')}:</Typography>
      <Typography variant="p8" fontWeight="bold">
        {formatShortId(job?.jobId)}
      </Typography>
      <Typography variant="p8">,</Typography>
      <Typography variant="p8">{t(`pipelines:${variant}_job`)}:</Typography>
      <Typography variant="p8" fontWeight="bold">
        {formatShortId(comparisonJobId)}
      </Typography>
    </Stack>
  );

  const renderButton = (
    comparisonJob: JobFragment,
    variant: 'target' | 'previous',
    component?: TTriggerComponentType,
  ) => {
    const label =
      component === 'MenuItem'
        ? t(`pipelines:compare_with_${variant}`)
        : undefined;

    const handleCompareArtifacts = () => {
      onCallback?.();
      navigate(
        `/devices/${job?.deviceIds?.[0]}/compare/${job?.jobId}/${comparisonJob.jobId}`,
      );
    };

    return (
      <Trigger
        component={component}
        tooltip={tooltip(comparisonJob.jobId, variant)}
        onClick={handleCompareArtifacts}
        icon={<CompareIcon />}
        buttonSize="extraSmall"
        buttonVariant="secondary"
        tooltipProps={{
          noMaxWidth: true,
          placement: component === 'MenuItem' ? 'right' : undefined,
        }}
        label={label ?? t('common:compare')}
      />
    );
  };

  const renderTrigger = () => (
    <Button
      icon={<CompareIcon />}
      size="extraSmall"
      variant="secondary"
      tooltip={t('pipelines:compare_artifacts')}
    >
      {t('common:compare')}
    </Button>
  );

  if (
    !canCompareTarget &&
    !canComparePrevious &&
    !canCompareVersions &&
    !canCompareSelectedVersions
  ) {
    return null;
  }

  if (canCompareSelectedVersions) {
    return (
      <CompareVersionsButton
        projectId={job!.projectIds![0]}
        baseVersion={job!.metaData!.created_project_version_id}
        targetVersion={selectedTargetJob!.metaData!.created_project_version_id}
        onCallback={onCallback}
      />
    );
  }

  if (canCompareVersions) {
    return <JobLogsCompareVersionsButton job={job} onCallback={onCallback} />;
  }

  if (canCompareTarget && canComparePrevious) {
    return (
      <PopoverController trigger={renderTrigger()}>
        {() => (
          <>
            {renderButton(resolvedTargetJob, 'target', 'MenuItem')}
            {renderButton(previousJob, 'previous', 'MenuItem')}
          </>
        )}
      </PopoverController>
    );
  }

  if (canComparePrevious) return renderButton(previousJob, 'previous');
  if (canCompareTarget) return renderButton(resolvedTargetJob, 'target');

  return cloneElement(renderTrigger(), {
    disabled: true,
    tooltip: t('device:no_artifacts_available'),
  });
};
